import * as z from "zod";
import { decimal, pagination } from "../index";
import { campaignInvitationDto, invitationStageCountsDto } from "./campaign";
import { contactMeasures } from "./engagement/enums";
import { accountTags, crmAccountWarmthDto, warmthBands } from "./insights";
import { accountStages } from "./source/enums";
import { orderFunnelStages } from "./source/order-funnel";

// The accounts of Guestlist (ADR-299): the organizations of the projection of crm that are customers, brands or both
// (ADR-292), never read from party. The list with its search and the territory of the area managers, the pickers of a
// campaign, and the card 360 of one account: its contacts, its invitations campaign by campaign and its orders.

/** What Guestlist needs of the port (ADR-287 §2.2): the stages of an order, for their words and tones. */
export type { OrderFunnelStage } from "./source/order-funnel";

const crmAccountKinds = ["customer", "brand"] as const;
export type CrmAccountKind = (typeof crmAccountKinds)[number];

/** A territory as party writes it (ADR-289): up to 100 characters. */
const territory = z.string().trim().min(1).max(100);

/** The accounts still in business, by name: those whose name holds `q`, of one kind or of both, of one territory. */
export const listCrmAccountsInput = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  kind: z.enum(crmAccountKinds).optional(),
  /** The sales territory of the country of the account, exactly (ADR-289). */
  territory: territory.optional(),
  /** The accounts with this tag (ADR-316 §1). */
  tag: z.enum(accountTags).optional(),
  /** By name (the default), or the warmest first (`rank_key`, ADR-316 §1). */
  sort: z.enum(["name", "warmth"]).optional(),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCrmAccountsInput = z.input<typeof listCrmAccountsInput>;

export const crmAccountDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    countryCode: z.string().nullable(),
    /** The sales territory of its country; null when the country has none. */
    territory: z.string().nullable(),
    /** The code of the customer on BC, when it has one. */
    customerCode: z.string().nullable(),
    isCustomer: z.boolean(),
    isBrand: z.boolean(),
  })
  .meta({ id: "CrmAccount" });
export type CrmAccountDto = z.output<typeof crmAccountDto>;

/** An account of the list, with its warmth (ADR-316 §1): null for an account without a customer's signal. */
export const crmAccountListItemDto = crmAccountDto
  .extend({ band: z.enum(warmthBands).nullable() })
  .meta({ id: "CrmAccountListItem" });
export type CrmAccountListItemDto = z.output<typeof crmAccountListItemDto>;

/** More territories than this is not a filter a screen offers: past it the read refuses rather than cut it. */
export const CRM_TERRITORIES_MAX = 500;

export const listCrmTerritoriesInput = z.object({});
export type ListCrmTerritoriesInput = z.input<typeof listCrmTerritoriesInput>;

/** The territories of the accounts still in business, by name: the values of the filter. */
export const crmTerritoryListDto = z.object({ items: z.array(z.string()) }).meta({ id: "CrmTerritoryList" });
export type CrmTerritoryListDto = z.output<typeof crmTerritoryListDto>;

export const getCrmAccountInput = z.object({ organizationId: z.uuid() });
export type GetCrmAccountInput = z.input<typeof getCrmAccountInput>;

/** How many of each part the card shows at most: the rest is in the screens of the campaigns, and later of a segment. */
export const CRM_ACCOUNT_CARD_LIMITS = { contacts: 100, campaigns: 10, orders: 25 } as const;

/**
 * A person of the account: who they are, what they do there, and how to reach them. Email, phone and job title can be
 * hidden by a rule of field (Q-SEC-41): missing, not null.
 */
export const crmAccountContactDto = z
  .object({
    id: z.uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    email: z.string().nullable().optional(),
    /** Since when the directory holds the email as not working (a hard bounce, ADR-320); null for a working one. */
    emailInvalidAt: z.iso.datetime().nullable().optional(),
    phone: z.string().nullable().optional(),
    /** Their business role, as the directory names it (buyer, owner, …). */
    role: z.string(),
    jobTitle: z.string().nullable().optional(),
    /**
     * What Guestlist measures of the person's emails (ADR-314 §5): opens and clicks, clicks only, counted without a
     * name, no emails. Missing for a delegated access.
     */
    measure: z.enum(contactMeasures).optional(),
  })
  .meta({ id: "CrmAccountContact" });
export type CrmAccountContactDto = z.output<typeof crmAccountContactDto>;

/**
 * A campaign the account is invited to, as a customer or as a brand: the stages of all those invitations, and the cells
 * of the ones it has as a customer, each for a brand.
 */
export const crmAccountCampaignDto = z
  .object({
    id: z.uuid(),
    name: z.string(),
    startDate: z.iso.date().nullable(),
    endDate: z.iso.date().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    stages: invitationStageCountsDto,
    invitations: z.array(campaignInvitationDto),
  })
  .meta({ id: "CrmAccountCampaign" });
export type CrmAccountCampaignDto = z.output<typeof crmAccountCampaignDto>;

/** An order of the account, as a customer or as a brand, in the currency of the order (ADR-290). */
export const crmAccountOrderDto = z
  .object({
    id: z.uuid(),
    orderNumber: z.string(),
    customerOrgId: z.uuid(),
    brandOrgId: z.uuid().nullable(),
    stage: z.enum(orderFunnelStages),
    orderedAt: z.iso.datetime().nullable(),
    currency: z.string().nullable(),
    orderedUnits: z.number().int().nullable(),
    shippedUnits: z.number().int().nullable(),
    orderedAmount: decimal.nullable(),
    /** What Business Central said it invoiced (I-06), null before it says. */
    invoicedAmount: decimal.nullable(),
  })
  .meta({ id: "CrmAccountOrder" });
export type CrmAccountOrderDto = z.output<typeof crmAccountOrderDto>;

/** Up to a limit, and whether there is more. */
const capped = <T extends z.ZodType>(item: T) => z.object({ items: z.array(item), hasMore: z.boolean() });

/**
 * The card 360 of an account (ADR-287 §5, ADR-299): who it is, its people, its invitations campaign by campaign (the
 * latest to start first) and its latest orders; with the names of the other accounts those name.
 */
export const crmAccountCardDto = crmAccountDto
  .extend({
    websiteDomain: z.string().nullable(),
    storeName: z.string().nullable(),
    /** Null when it is not a customer. */
    customerStage: z.enum(accountStages).nullable(),
    /** The account it was merged into: its contacts and its orders are there now. */
    mergedIntoId: z.uuid().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    contacts: capped(crmAccountContactDto),
    campaigns: capped(crmAccountCampaignDto),
    orders: capped(crmAccountOrderDto),
    /** The brands and the customers the invitations and the orders name, by id; empty while the projection lacks one. */
    names: z.array(z.object({ id: z.uuid(), name: z.string() })),
    /** How warm the customer is, and its people (ADR-316 §1); null for an account that is not a customer. */
    warmth: crmAccountWarmthDto.nullable(),
  })
  .meta({ id: "CrmAccountCard" });
export type CrmAccountCardDto = z.output<typeof crmAccountCardDto>;
